import { type EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import dynamic from 'next/dynamic'
import { type FC, type ReactNode, useCallback, useEffect, useState } from 'react'
import { useTheme } from 'styled-components'

import { CarouselBtnVariant } from '@/components/shared/carousel-buttons/CarouselButtons'
import {
  GalleryCountBadge,
  GalleryDotsContainer,
  GalleryDotsViewport,
  GalleryDotsWrapper,
  GallerySingleItemContainer,
  GallerySingleItemList,
  GallerySingleItemSlide,
  GallerySingleItemViewport,
} from '@/features/listing/components/gallery-single-item/styled'
import { BadgeSize } from '@/ui/atoms/badge'
import { hexToRgb } from '@/ui/helpers/hexToRgb'

const GalleryDotButton = dynamic(
  () =>
    import('@/features/listing/components/gallery-single-item/GalleryDotButton').then(
      (mod) => mod.GalleryDotButton
    ),
  {
    ssr: false,
  }
)
const NextButton = dynamic(
  () =>
    import('@/components/shared/carousel-buttons/CarouselButtons').then((mod) => mod.NextButton),
  {
    ssr: false,
  }
)
const PrevButton = dynamic(
  () =>
    import('@/components/shared/carousel-buttons/CarouselButtons').then((mod) => mod.PrevButton),
  {
    ssr: false,
  }
)

export type GallerySingleItemProps = {
  /**
   * name for generating unique keys
   */
  name: string
  /**
   * Tiles/Slides to be displayed in the carousel
   */
  slides: ReactNode[]
  /**
   * Override the total slides indicator if needed
   */
  overrideSlideCount?: number
  /**
   * Show prev/next arrows
   */
  showArrows?: boolean
  /**
   * Show dots
   */
  showDots?: boolean
}

export const GallerySingleItem: FC<GallerySingleItemProps> = ({
  name,
  slides,
  showArrows = false,
  showDots = false,
  overrideSlideCount,
}) => {
  const { colors } = useTheme()
  const slideCount = overrideSlideCount ?? slides.length
  const hasMultipleSlides = slideCount > 1

  const showcaseOptions: EmblaOptionsType = {
    active: true,
    containScroll: 'trimSnaps',
    dragFree: false,
    dragThreshold: 1,
    inViewThreshold: 1,
    skipSnaps: true,
    slidesToScroll: 1,
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(showcaseOptions)
  const [currentSlideInView, setCurrentSlideInView] = useState<number>(0)
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [dotsPosition, setDotsPosition] = useState(0)

  useEffect(() => {
    if (!emblaApi) return

    // hard reset carousel for options changes
    emblaApi.reInit()
  }, [emblaApi])

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev()
    }
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext()
    }
  }, [emblaApi])

  /* This updates the translate x value of the dots wrapper, animate the dots on carousel scroll */
  const handleUpdateDotsPosition = useCallback(() => {
    if (currentSlideInView <= 2) {
      setDotsPosition(0)
    } else if (currentSlideInView > 2 && currentSlideInView <= slides.length - 3) {
      setDotsPosition(-currentSlideInView + 2)
    }
  }, [currentSlideInView, slides])

  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    const onSelect = () => {
      const slidePosition = emblaApi.selectedScrollSnap()
      setCurrentSlideInView(slidePosition)

      setPrevBtnEnabled(emblaApi.canScrollPrev())
      setNextBtnEnabled(emblaApi.canScrollNext())
      setScrollSnaps(emblaApi.scrollSnapList())

      handleUpdateDotsPosition()
    }

    /** On change listener*/
    emblaApi.on('select', onSelect)
    /** On Load */
    onSelect()

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, slides, handleUpdateDotsPosition])

  return (
    <GallerySingleItemContainer>
      <GallerySingleItemViewport ref={emblaRef}>
        <GallerySingleItemList>
          {slides.map((slide, index) => (
            <GallerySingleItemSlide key={`${name}-slide-${index}`}>{slide}</GallerySingleItemSlide>
          ))}
        </GallerySingleItemList>
      </GallerySingleItemViewport>
      {showArrows && (
        <>
          <PrevButton
            onClick={scrollPrev}
            enabled={prevBtnEnabled}
            variant={CarouselBtnVariant.GALLERY_SINGLE_ITEM}
          />
          <NextButton
            onClick={scrollNext}
            enabled={nextBtnEnabled}
            variant={CarouselBtnVariant.GALLERY_SINGLE_ITEM}
          />
        </>
      )}
      {showDots && (
        <GalleryDotsContainer data-testid="carousel-dots">
          <GalleryDotsViewport>
            <GalleryDotsWrapper $transformValue={dotsPosition} $centerDots={slides.length < 5}>
              {scrollSnaps.map((_, index) => (
                <GalleryDotButton
                  index={index}
                  key={`${name}-dot-${index}`}
                  isSelected={index === currentSlideInView}
                  onClick={() => scrollTo(index)}
                />
              ))}
            </GalleryDotsWrapper>
          </GalleryDotsViewport>
        </GalleryDotsContainer>
      )}
      {hasMultipleSlides ? (
        <GalleryCountBadge
          size={BadgeSize.LARGE}
          backgroundColor={hexToRgb(colors.grey.primary, 0.7)}
          color={colors.white}
          isBorderless={true}
          label={`${currentSlideInView + 1} / ${slideCount}`}
        />
      ) : null}
    </GallerySingleItemContainer>
  )
}
